import type {
  ShearAnchorCheckDTO,
  ShearAnchorConnectorSelectionDTO,
  ShearAnchorLoadsDTO,
  ShearAnchorPosition,
} from '@api/fastAPI.schemas'
import { SHEAR_ANCHOR_POSITIONS } from 'src/components/manager/shearConnections'
import { ConnectorSelection, PRIORITY_LIST_SELECTION } from '../../connectorSelection'

export type ShearLoadCategory = 'Wind' | 'EarthQuake'

export const SHEAR_LOAD_CATEGORIES: ShearLoadCategory[] = ['Wind', 'EarthQuake']

export const shearLoadCategoryLabel: Record<ShearLoadCategory, string> = {
  Wind: 'Wind',
  EarthQuake: 'Erdbeben',
}

const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Formats a value in N or N/m as kN resp. kN/m with 2 decimals (de-DE). */
export const formatKilo = (value: number | undefined | null): string =>
  value == null ? '–' : decimalFormatter.format(value / 1000)

/** Formats a length in metres with 2 decimals and a unit (de-DE). */
export const formatMeters = (meters: number | undefined | null): string =>
  meters == null ? '–' : `${decimalFormatter.format(meters)} m`

/** Formats a plain number (a length in m, a utilization) with 2 decimals (de-DE). */
export const formatNumber = (value: number | undefined | null): string =>
  value == null ? '–' : decimalFormatter.format(value)

/** The check of each load category, keyed for column lookup. */
export const checksByLoadCategory = (
  checks: ShearAnchorCheckDTO[],
): Partial<Record<ShearLoadCategory, ShearAnchorCheckDTO>> => {
  const out: Partial<Record<ShearLoadCategory, ShearAnchorCheckDTO>> = {}
  for (const check of checks) {
    if (check.load_category === 'Wind' || check.load_category === 'EarthQuake') {
      out[check.load_category] = check
    }
  }
  return out
}

/**
 * The checks of each anchor position that has any, in position order — a segment is
 * anchored at its foot and at its head, each with its own Schubverbinder.
 */
export const checksByPosition = (
  checks: ShearAnchorCheckDTO[],
): { position: ShearAnchorPosition; checks: ShearAnchorCheckDTO[] }[] =>
  SHEAR_ANCHOR_POSITIONS.map(position => ({
    position,
    checks: checks.filter(check => check.position === position),
  })).filter(entry => entry.checks.length > 0)

/** The check with the highest utilization — the one the list row and the badge show. */
export const decisiveShearCheck = (
  checks: ShearAnchorCheckDTO[],
): ShearAnchorCheckDTO | undefined =>
  checks.reduce<ShearAnchorCheckDTO | undefined>(
    (max, check) => (max == null || check.utilization > max.utilization ? check : max),
    undefined,
  )

/**
 * Stable guid of an Einwirkungen load row — matches the table rows with the scene loads so the
 * eye column can hide them. Falls back to a synthesized per-element key when the original load
 * carries no guid (clipped line loads get a fresh one).
 */
export const shearAnchorLoadGuid = (
  loadGuid: string | null | undefined,
  elementGuid: string,
  kind: string,
  index: number,
): string => loadGuid ?? `${elementGuid}-${kind}-${index}`

/**
 * Maps the vertical loads standing on a stiffening segment to scene `DomainLoad`s so they can be
 * drawn with `SimpleLoadMesh` while the shear anchor is selected — the same treatment the tensile
 * anchor's relieving loads get. The DTO carries the full original loads; the domain is resolved
 * via `guidToDomain`, and loads without a resolvable domain or load case are skipped.
 *
 * Line loads are the clipped share standing on the segment, so the scene shows exactly what the
 * Einwirkungen table lists and what the friction is computed from.
 */
export const getShearAnchorDomainLoads = (
  loads: ShearAnchorLoadsDTO | undefined,
  guidToDomain: Record<string, Domain | undefined>,
  elementGuid: string,
): DomainLoad[] => {
  if (!loads) return []

  const toLoadCase = (value: {
    category?: string
    load_case_type?: string
  }): LoadCase | undefined => {
    if (!value?.category || !value.load_case_type) return undefined
    return {
      identifier: value.load_case_type,
      category: value.category as LoadCategory,
      load_case_type: value.load_case_type as LoadCaseTypes,
    }
  }

  const domainLoads: DomainLoad[] = []

  loads.vertical_point_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(contribution.load_case)
    if (!domain || !loadCase) return
    domainLoads.push({
      guid: shearAnchorLoadGuid(contribution.load_guid, elementGuid, 'vertical-point', index),
      element_guid: elementGuid,
      load_type: 'point-load',
      load_case: loadCase,
      force: dto.force,
      relative_position: Number(dto.relative_position),
      domain_guid: domain.guid,
      domain,
    })
  })

  loads.vertical_line_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(contribution.load_case)
    if (!domain || !loadCase) return
    domainLoads.push({
      guid: shearAnchorLoadGuid(contribution.load_guid, elementGuid, 'vertical-line', index),
      element_guid: elementGuid,
      load_type: 'line-load',
      load_case: loadCase,
      start: dto.start,
      end: dto.end,
      relative_start: Number(dto.relative_start ?? 0),
      relative_end: Number(dto.relative_end ?? 1),
      domain_guid: domain.guid,
      domain,
    })
  })

  return domainLoads
}

/** The saved Schubverbinder selection of one position; the prioritization when none is stored. */
export const savedShearAnchorSelection = (
  selections: ShearAnchorConnectorSelectionDTO[],
  position: ShearAnchorPosition,
): ConnectorSelection => {
  const saved = selections.find(candidate => candidate.position === position)
  return saved?.connectorSelectionPolicy === 'UserDefined'
    ? { policy: 'UserDefined', connectorId: saved.userDefinedConnectorId ?? null }
    : PRIORITY_LIST_SELECTION
}

/** Share of the wall height the scene band of a shear anchor covers at the wall's foot/head. */
const SHEAR_ANCHOR_BAND_HEIGHT_SHARE = 0.25

const EPSILON = 1e-6

interface Point3 {
  x: number
  y: number
  z: number
}

/** A wall polygon point in the wall's own plane: distance along the wall and height. */
interface WallPlanePoint {
  t: number
  z: number
}

/**
 * Highest (`top`) or lowest point of the polygon outline at distance `t`, approached from the
 * given side. Vertical edges are skipped, so at a step in the outline the two sides give the
 * heights below and above the step.
 */
const polygonEdgeZ = (
  polygon: WallPlanePoint[],
  t: number,
  side: 'before' | 'after',
  top: boolean,
): number | undefined => {
  let result: number | undefined
  polygon.forEach((a, index) => {
    const b = polygon[(index + 1) % polygon.length]
    const [from, to] = a.t <= b.t ? [a, b] : [b, a]
    if (to.t - from.t < EPSILON) return
    const covers =
      side === 'before'
        ? t > from.t + EPSILON && t <= to.t + EPSILON
        : t >= from.t - EPSILON && t < to.t - EPSILON
    if (!covers) return
    const z = from.z + ((t - from.t) / (to.t - from.t)) * (to.z - from.z)
    if (result === undefined || (top ? z > result : z < result)) result = z
  })
  return result
}

/**
 * Outline of the scene band marking a shear anchor: as wide as its stiffening segment and lying
 * along the wall's bottom (wall foot) or top (wall head) edge, so it is clear which wall, segment
 * and end the anchor belongs to. The interval is absolute metres along the wall from its first
 * point (same as `SimpleStiffeningMesh`). The edges are read from the wall polygon, so the band
 * follows sloped and stepped tops, and its height shrinks where the wall is lower than `height`
 * so it never leaves the wall.
 */
export const getShearAnchorBandPoints = (
  wallPoints: Point3[],
  segmentStart: number,
  segmentEnd: number,
  height: number,
  position: ShearAnchorPosition,
): Point3[] | undefined => {
  if (wallPoints.length < 2 || height <= 0 || segmentEnd <= segmentStart) return undefined
  const [wallStart, wallEnd] = wallPoints
  const dx = wallEnd.x - wallStart.x
  const dy = wallEnd.y - wallStart.y
  const wallLength = Math.hypot(dx, dy)
  if (wallLength <= 0) return undefined

  const polygon: WallPlanePoint[] = wallPoints.map(point => ({
    t: ((point.x - wallStart.x) * dx + (point.y - wallStart.y) * dy) / wallLength,
    z: point.z,
  }))
  const isTop = position === 'top'

  // Where the outline is sampled: the segment ends, and both sides of every polygon corner in
  // between, where the edge may kink or step.
  const samples: { t: number; side: 'before' | 'after' }[] = [
    { t: segmentStart, side: 'after' },
    ...[
      ...new Set(
        polygon
          .map(({ t }) => t)
          .filter(t => t > segmentStart + EPSILON && t < segmentEnd - EPSILON),
      ),
    ]
      .sort((a, b) => a - b)
      .flatMap(t => [
        { t, side: 'before' as const },
        { t, side: 'after' as const },
      ]),
    { t: segmentEnd, side: 'before' },
  ]

  const at = (t: number, z: number): Point3 => ({
    x: wallStart.x + (dx / wallLength) * t,
    y: wallStart.y + (dy / wallLength) * t,
    z,
  })

  const edge: Point3[] = []
  const inner: Point3[] = []
  let previous: { t: number; edgeZ: number; innerZ: number } | undefined
  for (const { t, side } of samples) {
    const topZ = polygonEdgeZ(polygon, t, side, true) ?? wallStart.z + height
    const bottomZ = polygonEdgeZ(polygon, t, side, false) ?? wallStart.z
    const bandHeight =
      Math.max(0, Math.min(height, topZ - bottomZ)) * SHEAR_ANCHOR_BAND_HEIGHT_SHARE
    const edgeZ = isTop ? topZ : bottomZ
    const innerZ = isTop ? topZ - bandHeight : bottomZ + bandHeight
    // Both sides of a corner without a step give the same point; keep it once.
    const isDuplicate =
      previous !== undefined &&
      Math.abs(previous.t - t) < EPSILON &&
      Math.abs(previous.edgeZ - edgeZ) < EPSILON &&
      Math.abs(previous.innerZ - innerZ) < EPSILON
    previous = { t, edgeZ, innerZ }
    if (isDuplicate) continue
    edge.push(at(t, edgeZ))
    inner.push(at(t, innerZ))
  }

  return [...edge, ...inner.reverse()]
}
